import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Truck, Package, Home, ChevronRight, LogOut } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_imageblaze/artifacts/5e5w021j_7D777DBC-744F-44B3-9C70-7AE1E6146D31.png";

const AdminHome = ({ user }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    b2c_new: 0,
    demenagement_new: 0,
    notifications: 0
  });

  // Redirect Colis Privé admin directly to B2B module
  useEffect(() => {
    if (user?.admin_level === 'colis_prive') {
      navigate('/admin/b2b', { replace: true });
    }
  }, [user, navigate]);

  // Si admin Colis Privé, afficher un écran de chargement pendant la redirection
  const isColisPriveAdmin = user?.admin_level === 'colis_prive';

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [b2cRes, demRes, notifRes] = await Promise.all([
        axios.get(`${API}/demandes-transport-b2c?status=nouveau`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/demandes-client?status=nouveau`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/notifications`, { withCredentials: true }).catch(() => ({ data: { unread_count: 0 } }))
      ]);
      
      setStats({
        b2c_new: Array.isArray(b2cRes.data) ? b2cRes.data.length : 0,
        demenagement_new: Array.isArray(demRes.data) ? demRes.data.length : 0,
        notifications: demRes.data?.unread_count || notifRes.data?.unread_count || 0
      });
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.removeItem('session_token');
    sessionStorage.clear();
    document.cookie = 'session_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    navigate('/login', { replace: true });
  };

  const allModules = [
    {
      id: 'b2b',
      title: 'Livraison B2B',
      subtitle: 'Gestion des livraisons professionnelles',
      description: 'Employés, planning, missions, formations, véhicules...',
      icon: Truck,
      color: 'slate',
      path: '/admin/b2b',
      badge: null,
      isPrimary: true
    },
    {
      id: 'b2c',
      title: 'Livraison B2C',
      subtitle: 'Transport pour particuliers',
      description: 'Demandes clients, devis, missions, planning...',
      icon: Package,
      color: 'blue',
      path: '/admin/b2c',
      badge: stats.b2c_new > 0 ? stats.b2c_new : null
    },
    {
      id: 'demenagement',
      title: 'Déménagement',
      subtitle: 'Service de déménagement',
      description: 'Demandes, devis, planning, équipes...',
      icon: Home,
      color: 'orange',
      path: '/admin/demenagement',
      badge: stats.demenagement_new > 0 ? stats.demenagement_new : null
    }
  ];

  // Filter modules for Colis Privé admin - only B2B visible (use existing isColisPriveAdmin)
  const modules = isColisPriveAdmin ? allModules.filter(m => m.id === 'b2b') : allModules;

  const colorClasses = {
    slate: {
      bg: 'bg-slate-800',
      bgLight: 'bg-slate-100',
      border: 'border-slate-800',
      text: 'text-slate-800',
      hover: 'hover:border-slate-900 hover:shadow-slate-200'
    },
    blue: {
      bg: 'bg-blue-600',
      bgLight: 'bg-blue-100',
      border: 'border-blue-500',
      text: 'text-blue-600',
      hover: 'hover:border-blue-600 hover:shadow-blue-200'
    },
    orange: {
      bg: 'bg-orange-500',
      bgLight: 'bg-orange-100',
      border: 'border-orange-500',
      text: 'text-orange-600',
      hover: 'hover:border-orange-600 hover:shadow-orange-200'
    }
  };

  // If Colis Privé admin, show loading then navigate
  if (isColisPriveAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-800 mx-auto mb-4"></div>
          <p className="text-slate-600">Redirection vers l'espace Colis Privé...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-[#0F172A] text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <img src={LOGO_URL} alt="DLV EXPRESS" className="h-10 w-auto" />
              <div className="hidden sm:block">
                <h1 className="text-lg font-semibold">Espace Administration</h1>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              {/* User info */}
              <div className="hidden sm:flex items-center gap-3">
                {user?.picture ? (
                  <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                ) : (
                  <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                    <span className="text-white font-medium text-sm">
                      {user?.name?.charAt(0)?.toUpperCase()}
                    </span>
                  </div>
                )}
                <span className="text-sm text-gray-300">{user?.name}</span>
              </div>
              
              {/* Logout */}
              <button
                onClick={handleLogout}
                className="p-2 text-red-400 hover:text-red-300 transition-colors"
                title="Déconnexion"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Welcome Section */}
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
            Bienvenue sur la plateforme de gestion
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Veuillez sélectionner l'activité que vous souhaitez gérer.
          </p>
        </div>

        {/* Module Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {modules.map((module) => {
            const colors = colorClasses[module.color];
            const Icon = module.icon;
            
            return (
              <button
                key={module.id}
                onClick={() => navigate(module.path)}
                className={`group relative bg-white rounded-2xl p-6 lg:p-8 shadow-lg border-2 ${colors.border} ${colors.hover} hover:shadow-xl transition-all duration-300 text-left`}
                data-testid={`module-${module.id}`}
              >
                {/* Badge for new items */}
                {module.badge && (
                  <span className="absolute -top-3 -right-3 w-8 h-8 bg-red-500 text-white text-sm font-bold rounded-full flex items-center justify-center animate-pulse shadow-lg">
                    {module.badge}
                  </span>
                )}
                
                {/* Primary badge */}
                {module.isPrimary && (
                  <span className="absolute top-4 right-4 px-2 py-1 bg-slate-800 text-white text-xs font-medium rounded-full">
                    Principal
                  </span>
                )}
                
                {/* Icon */}
                <div className={`w-16 h-16 ${colors.bgLight} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <Icon className={`w-8 h-8 ${colors.text}`} />
                </div>
                
                {/* Content */}
                <h3 className="text-xl lg:text-2xl font-bold text-slate-900 mb-2">
                  {module.title}
                </h3>
                <p className={`text-sm font-medium ${colors.text} mb-3`}>
                  {module.subtitle}
                </p>
                <p className="text-slate-500 text-sm mb-6">
                  {module.description}
                </p>
                
                {/* Action */}
                <div className={`flex items-center gap-2 ${colors.text} font-medium group-hover:gap-3 transition-all duration-300`}>
                  <span>Accéder</span>
                  <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </div>
              </button>
            );
          })}
        </div>

        {/* Quick Info */}
        <div className="mt-12 text-center text-slate-500 text-sm">
          <p>
            Sélectionnez une activité pour accéder à son interface de gestion dédiée.
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 mt-auto">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img src={LOGO_URL} alt="DLV EXPRESS" className="h-8 w-auto opacity-50" />
              <span className="text-slate-400 text-sm">© 2026 DLV Express</span>
            </div>
            <div className="text-slate-400 text-sm">
              Plateforme de gestion des activités
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AdminHome;
